import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import ModalDialog, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle
} from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Select, {
  type CSSObjectWithLabel,
  components as selectComponents,
  type MenuListComponentProps,
  type ValueType
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { LazyAnimatedRovoIcon } from '@atlassian/rovo-platform-ui-components/primitives/LazyAnimatedRovoIcon';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { HelperMessage, Label } from '@databases/form/FormComponents';
import { AiSummaryIcon } from '@databases/icons/AiSummaryIcon';
import { messages } from '@databases/intl/messages';
import {
  type AiSummaryFieldConfig,
  type AiSummaryOutputFormat,
  type DatabaseTextValue
} from '@databases/types/database';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { resolveRichTextValue } from '@canvas/adf/resolve-rich-text-value';
import { CommentRenderer } from '@canvas/editor/comment-renderer';
import { useMediaQuery } from '@canvas/hooks';

import {
  countAiSummaryPreviewGraphemes,
  createProgressiveAiSummaryPreview
} from './progressive-ai-summary-preview.ts';

type FormatOption = { label: string; value: AiSummaryOutputFormat };

export type AiSummaryPreviewOption = {
  label: string;
  description?: string;
  entryId: string;
  value: number;
};

export type AiSummaryPreviewRequest = {
  abortSignal: AbortSignal;
  config: AiSummaryFieldConfig;
  entryId: string;
};

type AiSummaryPreviewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | {
      kind: 'revealing';
      document: ReturnType<typeof resolveRichTextValue>;
      summary: DatabaseTextValue;
      visibleGraphemeCount: number;
      totalGraphemeCount: number;
    }
  | { kind: 'success'; summary: DatabaseTextValue };

const EMPTY_PREVIEW_OPTIONS: AiSummaryPreviewOption[] = [];
const MAX_PREVIEW_ENTRY_COUNT = 5;
const PREVIEW_GRAPHEME_INTERVAL_MS = 20;

export interface AiSummaryFieldModalProps {
  mode: 'add' | 'edit';
  initialConfig?: AiSummaryFieldConfig;
  previewOptions?: AiSummaryPreviewOption[];
  onClose: (details: {
    config: AiSummaryFieldConfig;
    exitAction: 'closeButton' | 'escape' | 'outsideClick';
  }) => void;
  onPreviewMenuOpen?: () => void;
  onRegenerate?: (format: AiSummaryOutputFormat) => void;
  onGeneratePreview?: (
    request: AiSummaryPreviewRequest
  ) => Promise<DatabaseTextValue | null>;
  onPreviewGenerated?: (config: AiSummaryFieldConfig) => void;
  onPreviewCleared?: () => void;
  onSaveAndRun?: (config: AiSummaryFieldConfig) => void;
  onSaveWithoutRunning?: (config: AiSummaryFieldConfig) => void;
  isSaveAndRunDisabled?: boolean;
}

const contentStyles = xcss({
  maxHeight: '100%',
  overflowY: 'auto'
});

const previewSectionStyles = xcss({
  borderBlockEndColor: 'color.border',
  borderBlockEndStyle: 'solid',
  borderBlockEndWidth: 'border.width',
  paddingBlockEnd: 'space.150'
});

const previewOutputStyles = xcss({
  backgroundColor: 'elevation.surface.sunken',
  borderRadius: 'radius.large',
  color: 'color.text',
  marginBlockStart: 'space.200',
  maxHeight: '120px',
  overflowY: 'auto',
  padding: 'space.200',
  width: '550px'
});

const permissionsNoticeStyles = xcss({
  backgroundColor: 'color.background.information',
  borderRadius: 'radius.large',
  padding: 'space.150'
});

const previewMenuHeaderStyles = xcss({
  paddingBlock: 'space.100',
  paddingInline: 'space.150'
});

const previewSelectContainerStyles = xcss({
  flexGrow: 1,
  minWidth: 'space.0'
});

const compactSelectStyles = {
  menuPortal: (base: CSSObjectWithLabel) => ({
    ...base,
    zIndex: 750
  }),
  control: (base: CSSObjectWithLabel, state: { isFocused: boolean }) => ({
    ...base,
    border: `1px solid ${
      state.isFocused ? token('color.border.focused') : token('color.border')
    }`,
    borderRadius: token('radius.medium', '6px'),
    height: '40px',
    minHeight: '40px'
  }),
  valueContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: `${token('space.0')} ${token('space.150')}`
  }),
  indicatorsContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    height: '100%'
  }),
  dropdownIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: `${token('space.0')} ${token('space.100')}`
  })
};

const AiSummaryFieldModal: FunctionComponent<AiSummaryFieldModalProps> = ({
  mode,
  initialConfig,
  previewOptions = EMPTY_PREVIEW_OPTIONS,
  onClose,
  onPreviewMenuOpen,
  onRegenerate,
  onGeneratePreview,
  onPreviewGenerated,
  onPreviewCleared,
  onSaveAndRun,
  onSaveWithoutRunning,
  isSaveAndRunDisabled = false
}) => {
  const { formatMessage } = useIntl();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [format, setFormat] = useState<AiSummaryOutputFormat>(
    initialConfig?.outputFormat ?? 'paragraph'
  );
  const [selectedPreviewOptionValue, setSelectedPreviewOptionValue] = useState<
    number | null
  >(null);
  const [previewState, setPreviewState] = useState<AiSummaryPreviewState>({
    kind: 'idle'
  });
  const previewAbortControllerRef = useRef<AbortController | null>(null);
  const previewOutputRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollCompletedPreviewRef = useRef(false);
  const previewStateKind = previewState.kind;

  const formatOptions = useMemo<FormatOption[]>(
    () => [
      {
        label: formatMessage(messages['field-settings.ai-summary.format-one-liner']),
        value: 'one-liner'
      },
      {
        label: formatMessage(messages['field-settings.ai-summary.format-bullet-points']),
        value: 'bullet-points'
      },
      {
        label: formatMessage(messages['field-settings.ai-summary.format-paragraph']),
        value: 'paragraph'
      }
    ],
    [formatMessage]
  );

  const config = useMemo<AiSummaryFieldConfig>(
    () => ({
      outputFormat: format
    }),
    [format]
  );

  const selectedPreviewOption =
    previewOptions.find((option) => option.value === selectedPreviewOptionValue) ?? null;
  const previewMenuPortalTarget =
    typeof globalThis.document === 'undefined' ? undefined : globalThis.document.body;

  const previewSelectComponents = useMemo(
    () => ({
      MenuList: (props: MenuListComponentProps<AiSummaryPreviewOption>) => (
        <selectComponents.MenuList {...props}>
          <Box xcss={previewMenuHeaderStyles}>
            <Text color="color.text.subtle" size="small" weight="bold">
              {formatMessage(
                messages['field-settings.ai-summary.preview-entries-header.non-final'],
                { count: Math.min(previewOptions.length, MAX_PREVIEW_ENTRY_COUNT) }
              )}
            </Text>
          </Box>
          {props.children}
        </selectComponents.MenuList>
      )
    }),
    [formatMessage, previewOptions.length]
  );

  const resetPreview = useCallback(() => {
    previewAbortControllerRef.current?.abort();
    previewAbortControllerRef.current = null;
    if (previewState.kind === 'revealing' || previewState.kind === 'success') {
      onPreviewCleared?.();
    }
    setPreviewState({ kind: 'idle' });
  }, [onPreviewCleared, previewState.kind]);

  useEffect(
    () => () => {
      previewAbortControllerRef.current?.abort();
      previewAbortControllerRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (previewState.kind !== 'revealing') {
      return;
    }

    if (prefersReducedMotion) {
      setPreviewState({
        kind: 'success',
        summary: previewState.summary
      });
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      setPreviewState((currentState) => {
        if (currentState.kind !== 'revealing') {
          return currentState;
        }

        const visibleGraphemeCount = currentState.visibleGraphemeCount + 1;
        return visibleGraphemeCount >= currentState.totalGraphemeCount
          ? {
              kind: 'success',
              summary: currentState.summary
            }
          : { ...currentState, visibleGraphemeCount };
      });
    }, PREVIEW_GRAPHEME_INTERVAL_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [prefersReducedMotion, previewState]);

  useEffect(() => {
    const shouldAutoScroll =
      previewStateKind === 'revealing' ||
      (previewStateKind === 'success' && shouldAutoScrollCompletedPreviewRef.current);

    shouldAutoScrollCompletedPreviewRef.current = previewStateKind === 'revealing';

    const previewOutput = previewOutputRef.current;
    if (!shouldAutoScroll || !previewOutput) {
      return;
    }

    const scrollToLatestContent = () => {
      previewOutput.scrollTop = previewOutput.scrollHeight;
    };
    scrollToLatestContent();
    const mutationObserver = new MutationObserver(scrollToLatestContent);
    mutationObserver.observe(previewOutput, {
      childList: true,
      characterData: true,
      subtree: true
    });
    const animationFrameId = globalThis.requestAnimationFrame(scrollToLatestContent);

    return () => {
      mutationObserver.disconnect();
      globalThis.cancelAnimationFrame(animationFrameId);
    };
  }, [previewStateKind]);

  const handleFormatChange = useCallback(
    (option: ValueType<FormatOption, false>) => {
      if (option) {
        resetPreview();
        setFormat(option.value);
      }
    },
    [resetPreview]
  );

  const handlePreviewOptionChange = useCallback(
    (option: ValueType<AiSummaryPreviewOption, false>) => {
      resetPreview();
      setSelectedPreviewOptionValue(option?.value ?? null);
    },
    [resetPreview]
  );

  const handleGeneratePreview = useCallback(() => {
    if (!selectedPreviewOption) {
      return;
    }

    if (!onGeneratePreview) {
      onRegenerate?.(format);
      return;
    }

    previewAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    previewAbortControllerRef.current = abortController;
    setPreviewState({ kind: 'loading' });

    void onGeneratePreview({
      abortSignal: abortController.signal,
      config,
      entryId: selectedPreviewOption.entryId
    }).then(
      (summary) => {
        if (
          previewAbortControllerRef.current === abortController &&
          !abortController.signal.aborted
        ) {
          previewAbortControllerRef.current = null;
          if (summary === null) {
            setPreviewState({ kind: 'idle' });
          } else {
            const document = resolveRichTextValue(summary);
            const totalGraphemeCount = countAiSummaryPreviewGraphemes(document);
            setPreviewState(
              prefersReducedMotion || totalGraphemeCount <= 1
                ? { kind: 'success', summary }
                : {
                    kind: 'revealing',
                    document,
                    summary,
                    visibleGraphemeCount: 1,
                    totalGraphemeCount
                  }
            );
          }
          if (summary !== null) {
            onPreviewGenerated?.(config);
          }
        }
      },
      () => {
        if (
          previewAbortControllerRef.current === abortController &&
          !abortController.signal.aborted
        ) {
          previewAbortControllerRef.current = null;
          setPreviewState({ kind: 'idle' });
        }
      }
    );
  }, [
    config,
    format,
    onGeneratePreview,
    onPreviewGenerated,
    onRegenerate,
    prefersReducedMotion,
    selectedPreviewOption
  ]);

  const previewDocument =
    previewState.kind === 'revealing'
      ? createProgressiveAiSummaryPreview(
          previewState.document,
          previewState.visibleGraphemeCount
        )
      : previewState.kind === 'success'
        ? resolveRichTextValue(previewState.summary)
        : undefined;

  const handleSaveWithoutRunning = useCallback(() => {
    onSaveWithoutRunning?.(config);
  }, [config, onSaveWithoutRunning]);

  const handleSaveAndRun = useCallback(() => {
    onSaveAndRun?.(config);
  }, [config, onSaveAndRun]);

  return (
    <ModalDialog
      height="auto"
      width={600}
      onClose={(event) => {
        const exitAction =
          'key' in event && event.key === 'Escape'
            ? 'escape'
            : event.target instanceof Element &&
                event.target.closest(
                  '[data-testid="ai-summary-field-modal--close-button"]'
                )
              ? 'closeButton'
              : 'outsideClick';
        onClose({ config, exitAction });
      }}
      testId="ai-summary-field-modal"
    >
      <ModalHeader hasCloseButton>
        <ModalTitle>
          <Inline alignBlock="center" space="space.100">
            <AiSummaryIcon label="" testId="ai-summary-field-icon" />
            <Heading as="h1" size="medium">
              {formatMessage(
                messages[
                  mode === 'edit'
                    ? 'field-settings.ai-summary.edit-modal-title'
                    : 'field-settings.ai-summary.modal-title'
                ]
              )}
            </Heading>
            <IconButton
              icon={InformationCircleIcon}
              appearance="subtle"
              spacing="compact"
              label={formatMessage(
                messages['field-settings.ai-summary.modal-description']
              )}
              isTooltipDisabled={false}
              tooltip={{ position: 'bottom' }}
              testId="ai-summary-field-info"
            />
          </Inline>
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Box xcss={contentStyles}>
          <Stack space="space.150">
            <Stack space="space.200" testId="ai-summary-form">
              <Stack space="space.100">
                <Label id="ai-summary-format-label" htmlFor="ai-summary-format">
                  {formatMessage(messages['field-settings.ai-summary.format'])}
                </Label>
                <Select<FormatOption>
                  inputId="ai-summary-format"
                  options={formatOptions}
                  value={formatOptions.find((option) => option.value === format) ?? null}
                  onChange={handleFormatChange}
                  isSearchable={false}
                  styles={compactSelectStyles}
                />
              </Stack>
              <Box xcss={previewSectionStyles}>
                <Stack space="space.100">
                  <Label
                    id="ai-summary-preview-with-label"
                    htmlFor="ai-summary-preview-with"
                  >
                    {formatMessage(messages['field-settings.ai-summary.preview-with'])}
                  </Label>
                  <Inline alignBlock="center" space="space.100">
                    <Box
                      testId="ai-summary-preview-select"
                      xcss={previewSelectContainerStyles}
                    >
                      <Select<AiSummaryPreviewOption>
                        inputId="ai-summary-preview-with"
                        components={previewSelectComponents}
                        options={previewOptions}
                        value={selectedPreviewOption}
                        onChange={handlePreviewOptionChange}
                        onMenuOpen={onPreviewMenuOpen}
                        menuPortalTarget={previewMenuPortalTarget}
                        menuPosition="fixed"
                        placeholder={formatMessage(
                          messages['field-settings.ai-summary.preview-placeholder']
                        )}
                        formatOptionLabel={(option, { context }) => (
                          <>
                            {context === 'menu' && option.description ? (
                              <Stack space="space.050">
                                <Text maxLines={1}>{option.label}</Text>
                                <Text color="color.text.subtle" maxLines={1} size="small">
                                  {option.description}
                                </Text>
                              </Stack>
                            ) : (
                              <Text maxLines={1}>{option.label}</Text>
                            )}
                          </>
                        )}
                        isSearchable={false}
                        styles={compactSelectStyles}
                      />
                    </Box>
                    <Button
                      isDisabled={
                        previewState.kind === 'loading' ||
                        !selectedPreviewOption ||
                        (!onRegenerate && !onGeneratePreview)
                      }
                      onClick={handleGeneratePreview}
                      style={{
                        alignItems: 'center',
                        borderRadius: token('radius.medium', '6px'),
                        height: '40px'
                      }}
                      testId="ai-summary-regenerate"
                    >
                      {formatMessage(messages['field-settings.ai-summary.regenerate'])}
                    </Button>
                  </Inline>
                  <HelperMessage>
                    {formatMessage(messages['field-settings.ai-summary.preview-helper'])}
                  </HelperMessage>
                  {previewState.kind !== 'idle' && (
                    <Box
                      aria-live={previewState.kind === 'revealing' ? 'off' : undefined}
                      ref={previewOutputRef}
                      role="status"
                      testId="ai-summary-preview-status"
                      xcss={previewOutputStyles}
                    >
                      {previewState.kind === 'loading' ? (
                        <Inline alignBlock="center" space="space.100">
                          {prefersReducedMotion ? (
                            <AskRovoIcon height={24} width={24} noPadding />
                          ) : (
                            <LazyAnimatedRovoIcon
                              alt=""
                              height="24px"
                              testId="ai-summary-animated-rovo-icon"
                              width="24px"
                            />
                          )}
                          <Text>
                            {formatMessage(
                              messages['field-settings.ai-summary.generating']
                            )}
                          </Text>
                        </Inline>
                      ) : (
                        <Stack space="space.100">
                          <Inline alignBlock="center" space="space.100">
                            <AlignTextLeftIcon
                              label=""
                              testId="ai-summary-preview-output-icon"
                            />
                            <Heading as="h2" color="color.text" size="xxsmall">
                              {formatMessage(
                                messages['field-settings.ai-summary.preview-heading']
                              )}
                            </Heading>
                          </Inline>
                          <CommentRenderer document={previewDocument} alignment="left" />
                        </Stack>
                      )}
                    </Box>
                  )}
                </Stack>
              </Box>
            </Stack>
            <Box testId="ai-summary-permissions-notice" xcss={permissionsNoticeStyles}>
              <Inline alignBlock="start" space="space.150">
                <StatusInformationIcon
                  color={token('color.icon.information')}
                  label=""
                  spacing="spacious"
                />
                <Text>
                  {formatMessage(
                    messages['field-settings.ai-summary.permissions-notice']
                  )}
                </Text>
              </Inline>
            </Box>
          </Stack>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Inline space="space.100" alignBlock="center">
          <Button
            appearance="subtle"
            onClick={handleSaveWithoutRunning}
            testId="ai-summary-save-without-running"
          >
            {formatMessage(
              messages[
                mode === 'edit'
                  ? 'field-settings.ai-summary.save-without-running'
                  : 'field-settings.ai-summary.add-as-empty-field'
              ]
            )}
          </Button>
          <Button
            appearance="primary"
            isDisabled={isSaveAndRunDisabled}
            onClick={handleSaveAndRun}
            testId="ai-summary-save-and-run"
          >
            {formatMessage(
              messages[
                mode === 'edit'
                  ? 'field-settings.ai-summary.save-and-rerun'
                  : 'field-settings.ai-summary.add-and-generate'
              ]
            )}
          </Button>
        </Inline>
      </ModalFooter>
    </ModalDialog>
  );
};

export default AiSummaryFieldModal;
